import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Handshake, Layers } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { nomeDaEmpresa, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Estado, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Kanban } from '../../componentes/kanban';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, dinheiroPorMoeda, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { uuidDaUrl } from '../../componentes/filtros-lista';
import { GerirFunis, useFunis } from './Funis';
import { DrawerOportunidade } from './DrawerOportunidade';
import { ModalGanhar, ModalPerder } from './Encerrar';
import { FormOportunidade, useEquipes } from './FormOportunidade';
import { Kpi } from './Kpi';
import type { Oportunidade, Quadro as DadosQuadro } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { anunciarOcultos } from '../../componentes/sensivel';

function Metricas({ q }: { q: DadosQuadro }) {
  const { t } = useTranslation('pipeline');
  const m = q.metricas;
  // Lista de 04/11, item 3: nunca somando moedas; sem a lista (servidor anterior), o número de antes.
  const valores = m.valores_em_negociacao ?? [];
  const emNegociacao = valores.length ? dinheiroPorMoeda(valores.map((x) => ({ moeda: x.moeda, valor: x.valor_centavos }))) : dinheiro(m.valor_em_negociacao_centavos);
  const ponderado = valores.length ? dinheiroPorMoeda(valores.map((x) => ({ moeda: x.moeda, valor: x.ponderado_centavos }))) : dinheiro(m.valor_ponderado_centavos);
  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi rotulo={t('metricas.valorEmNegociacao')} valor={emNegociacao} detalhe={t('metricas.ponderado', { valor: ponderado })} cor="ciano" />
        <Kpi rotulo={t('metricas.abertas')} valor={numero(m.abertas)} />
        <Kpi rotulo={t('metricas.cicloMedio')} valor={m.ciclo_medio_dias === null ? '-' : t('metricas.dias', { count: m.ciclo_medio_dias, valor: numero(m.ciclo_medio_dias, 1) })} detalhe={m.ciclo_medio_dias === null ? t('metricas.semCiclo') : undefined} />
        <Kpi rotulo={t('campos.ganhas')} valor={numero(m.ganhas)} cor="verde" />
      </div>
      <Cartao titulo={t('metricas.conversao')}>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {m.conversao_etapas.map((c) => (
            <li key={c.etapa_id} className="min-w-0">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="truncate font-medium" title={rotuloEtapaPipeline(c.nome)}>{rotuloEtapaPipeline(c.nome)}</span>
                <span className="numero text-tinta-2">{c.taxa === null ? '-' : `${c.taxa}%`}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-sup-3" role="presentation">
                <div className="h-1.5 rounded-full bg-ciano" style={{ width: `${c.taxa ?? 0}%` }} />
              </div>
              <p className="mt-1 text-xs text-tinta-3">{c.entraram ? t('metricas.conversaoTexto', { avancaram: c.avancaram, entraram: c.entraram }) : t('metricas.semDados')}</p>
            </li>
          ))}
        </ul>
      </Cartao>
    </div>
  );
}

/**
 * V3 verticais: todas as verticais em que o lead é trabalhado, uma a uma (a que a pessoa não vê, como "Outra empresa";
 * vazia, "Todas"). Com uma empresa só no workspace, nada aparece.
 */
function VerticaisDoCartao({ empresas }: { empresas: readonly string[] | undefined }) {
  const { t } = useTranslation();
  const catalogo = useEmpresasDoCatalogo().data?.empresas ?? [];
  if (!empresas || catalogo.length < 2) return null;
  if (!empresas.length) return <Selo cor="neutro">{t('verticais.todas')}</Selo>;
  return (
    <div className="flex flex-wrap gap-1" title={t('verticais.titulo')}>
      {empresas.map((id) => {
        const e = catalogo.find((x) => x.id === id);
        return (
          <Selo key={id} cor="ciano" quebra>
            {e ? nomeDaEmpresa(e) : t('verticais.outra')}
          </Selo>
        );
      })}
    </div>
  );
}

function CartaoOportunidade({ o }: { o: Oportunidade }) {
  const { t } = useTranslation('pipeline');
  return (
    <div className="flex flex-col gap-1.5">
      <p className="leading-snug font-medium">{textoLivre(o.titulo, o.parceiro_nome)}</p>
      <p className="truncate text-xs text-tinta-3" title={nomePessoa(o.parceiro_nome) || undefined}>{nomePessoa(o.parceiro_nome)}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="numero text-sm font-semibold">{anunciarOcultos(dinheiro(o.valor_centavos, o.moeda))}</span>
        <span className="numero text-xs text-tinta-3">{o.probabilidade}%</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-tinta-3">
        <TextoCortado className="">{o.responsavel_nome ?? t('quadro.semResponsavel')}</TextoCortado>
        {o.previsao_fechamento && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="size-3.5" aria-hidden />
            <span className="numero">{data(o.previsao_fechamento)}</span>
          </span>
        )}
      </div>
      {o.atividades_atrasadas > 0 ? <Selo cor="laranja">{t('quadro.atrasadas', { count: o.atividades_atrasadas })}</Selo> : o.atividades_pendentes > 0 ? <Selo cor="azul">{t('quadro.pendentes', { count: o.atividades_pendentes })}</Selo> : null}
      {/* Item 13 (21/10): as verticais (empresas) em selos no rodapé do card. */}
      <VerticaisDoCartao empresas={o.empresas} />
    </div>
  );
}

/** U13: o funil escolhido fica no endereço (`?funil=`) e, por pessoa e workspace, no aparelho (como o período do Início). */
const chaveFunil = (usuarioId: string, workspaceId: string) => `malgoos.pipeline.funil.${usuarioId}.${workspaceId}`;
function lerFunilGuardado(chave: string) {
  try {
    return uuidDaUrl(localStorage.getItem(chave));
  } catch {
    return '';
  }
}
function guardarFunil(chave: string, id: string) {
  try {
    localStorage.setItem(chave, id);
  } catch {
    // Sem armazenamento (aba privada): o endereço segue valendo.
  }
}

/** M2.6 aba Pipeline: kanban com arrastar entre etapas, métricas no topo e drawer da oportunidade. U13: um funil por vez. */
export function Quadro() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const abertaId = params.get('oportunidade');
  const [soMinhas, setSoMinhas] = useState(false);
  const [equipe, setEquipe] = useState('');
  const [chave, setChave] = useState(0);
  const [ganhando, setGanhando] = useState<Oportunidade | null>(null);
  const [perdendo, setPerdendo] = useState<Oportunidade | null>(null);
  const [novo, setNovo] = useState(false);
  const [gerindo, setGerindo] = useState(false);
  const equipes = useEquipes();
  const funis = useFunis();
  const guardado = chaveFunil(s.usuario.id, s.workspace.id);
  // O da URL, senão o guardado, valendo só se estiver entre os ativos; senão o padrão.
  const ativos = funis.data?.itens ?? [];
  const pedido = uuidDaUrl(params.get('funil')) || lerFunilGuardado(guardado);
  const funilId = ativos.find((f) => f.id === pedido)?.id ?? ativos.find((f) => f.padrao)?.id ?? '';
  const escolherFunil = (id: string) => {
    guardarFunil(guardado, id);
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('funil', id);
        return n;
      },
      { replace: true },
    );
  };
  useEffect(() => {
    if (funilId) guardarFunil(guardado, funilId);
  }, [funilId, guardado]);
  const q = useQuery({
    queryKey: ['pipeline', 'quadro', funilId, soMinhas, equipe],
    queryFn: () => get<DadosQuadro>('/pipeline/quadro', { funil_id: funilId, responsavel_id: soMinhas ? s.usuario.id : undefined, equipe_id: equipe || undefined }),
    enabled: !!funilId,
  });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  // O Kanban guarda a coluna otimista de cada cartão; remontar devolve o cartão ao lugar que o servidor tem.
  const remontar = () => setChave((k) => k + 1);

  const mover = useMutation({
    mutationFn: (v: { id: string; etapa_id: string }) => post(`/pipeline/oportunidades/${v.id}/mover`, { etapa_id: v.etapa_id }),
    onSuccess: recarregar,
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      remontar();
      await recarregar();
    },
  });

  const abrir = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('oportunidade', id);
        else n.delete('oportunidade');
        return n;
      },
      { replace: true },
    );

  const pode = s.pode('pipeline.editar');
  // Val5 (29/10): soltar na coluna de venda ou perda pede `pipeline.ganhar_perder`; reordenar e gerir funis, `pipeline.configurar`.
  const podeEncerrar = s.pode('pipeline.ganhar_perder');
  // U13: a ordem das colunas é a das etapas do funil (POST /pipeline/etapas/reordenar).
  const reordenar = useMutation({
    mutationFn: (ids: string[]) => post('/pipeline/etapas/reordenar', { funil_id: funilId, ids }),
    onSuccess: recarregar,
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      remontar();
      await recarregar();
    },
  });

  return (
    <>
      {(s.pode('pipeline.criar') || s.pode('pipeline.excluir')) && (
        <AcaoPrimaria>
          {s.pode('pipeline.excluir') && <BotaoLixeira entidades={['pipeline.oportunidade']} />}
          {s.pode('pipeline.criar') && (
            <Botao variante="primario" icone={<Handshake className="size-4" />} onClick={() => setNovo(true)}>
              {t('acoes.novaOportunidade')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-56">
          <Selecao aria-label={t('funis.funil')} opcoes={ativos.map((f) => ({ valor: f.id, rotulo: rotuloFunilPipeline(f.nome) }))} value={funilId} onChange={(e) => e.target.value && escolherFunil(e.target.value)} />
        </div>
        {podeNoWorkspace(s, 'pipeline.configurar') && (
          <Botao variante="secundario" icone={<Layers className="size-4" />} onClick={() => setGerindo(true)}>
            {t('funis.gerir')}
          </Botao>
        )}
        <div className="w-full sm:w-56">
          <Selecao aria-label={t('campos.equipe')} opcoes={(equipes.data?.itens ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))} vazio={`${t('campos.equipe')}: ${tn('comum.todas')}`} value={equipe} onChange={(e) => setEquipe(e.target.value)} />
        </div>
        <Botao variante="secundario" className={soMinhas ? 'border-2 border-acento-forte text-acento-forte' : ''} aria-pressed={soMinhas} onClick={() => setSoMinhas((v) => !v)}>
          {t('acoes.minhas')}
        </Botao>
      </div>
      {/* Sem nenhum funil ativo o quadro não tem o que mostrar: o caminho é criar o primeiro funil. */}
      {funis.isSuccess && ativos.length === 0 ? (
        <Cartao semPadding>
          <Vazio
            texto={t('funis.vazioQuadro')}
            acao={
              podeNoWorkspace(s, 'pipeline.configurar') ? (
                <Botao icone={<Layers className="size-4" />} onClick={() => setGerindo(true)}>
                  {t('funis.gerir')}
                </Botao>
              ) : undefined
            }
          />
        </Cartao>
      ) : (
      /* Com a lista de funis em erro, o quadro (que espera o funil) mostra esse erro em vez de carregar para sempre. */
      <Estado consulta={funis.isError ? { isPending: false, isError: true, error: funis.error, data: undefined, refetch: funis.refetch } : q}>
        {(d) => (
          <>
            <Metricas q={d} />
            {d.cartoes.truncado && (
              <p role="status" className="mb-3 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-2">
                {t('quadro.truncado', { exibidos: numero(d.cartoes.exibidos), total: numero(d.cartoes.total) })}
              </p>
            )}
            {d.oportunidades.length === 0 ? (
              <Cartao semPadding>
                <Vazio
                  texto={t('quadro.vazio')}
                  acao={
                    s.pode('pipeline.criar') ? (
                      <Botao icone={<Handshake className="size-4" />} onClick={() => setNovo(true)}>
                        {t('acoes.novaOportunidade')}
                      </Botao>
                    ) : undefined
                  }
                />
              </Cartao>
            ) : (
              <Kanban<Oportunidade>
                key={chave}
                colunas={d.etapas.map((e) => ({
                  id: e.id,
                  titulo: rotuloEtapaPipeline(e.nome),
                  // Contador e valor reais da etapa; só avisa quando o quadro mostra menos que isso.
                  rodape: (
                    <>
                      <span className="block">
                        {t('quadro.totalColuna', { n: numero(e.oportunidades) })} · {anunciarOcultos(e.valores?.length ? dinheiroPorMoeda(e.valores) : dinheiro(e.valor_centavos))}
                      </span>
                      {(e.exibidas ?? e.oportunidades) < e.oportunidades && (
                        <span className="block">{t(e.tipo === 'aberta' ? 'quadro.exibindo' : 'quadro.exibindoRecentes', { n: numero(e.exibidas ?? 0) })}</span>
                      )}
                    </>
                  ),
                }))}
                itens={d.oportunidades}
                colunaDe={(o) => o.etapa_id}
                render={(o) => <CartaoOportunidade o={o} />}
                aoClicar={(o) => abrir(o.id)}
                rotulo={(o) => textoLivre(o.titulo, o.parceiro_nome)}
                aoMover={(o, destino) => {
                  const etapa = d.etapas.find((e) => e.id === destino);
                  if (!pode || !etapa) return remontar();
                  // Lista de 30/10 (B), item 8: a pausada não anda de etapa nem encerra antes de ser retomada.
                  if (o.pausada_em && o.estado === 'aberta') {
                    toast.erro(t('crm.pausa.naoMove'));
                    return remontar();
                  }
                  // Ganhar e perder têm confirmação própria (motivo obrigatório, conversão em projeto).
                  if ((etapa.tipo === 'ganha' || etapa.tipo === 'perdida') && !podeEncerrar) return remontar();
                  if (etapa.tipo === 'ganha') {
                    remontar();
                    return setGanhando(o);
                  }
                  if (etapa.tipo === 'perdida') {
                    remontar();
                    return setPerdendo(o);
                  }
                  mover.mutate({ id: o.id, etapa_id: destino });
                }}
                aoReordenarColunas={podeNoWorkspace(s, 'pipeline.configurar') ? (ids) => reordenar.mutate(ids) : undefined}
              />
            )}
          </>
        )}
      </Estado>
      )}

      {abertaId && <DrawerOportunidade key={abertaId} id={abertaId} aoFechar={() => abrir(null)} aoMudar={() => void recarregar()} />}
      {ganhando && (
        <ModalGanhar
          oportunidade={ganhando}
          aoFechar={() => setGanhando(null)}
          aoConcluir={async () => {
            setGanhando(null);
            remontar();
            await recarregar();
          }}
        />
      )}
      {perdendo && (
        <ModalPerder
          oportunidade={perdendo}
          aoFechar={() => setPerdendo(null)}
          aoConcluir={async () => {
            setPerdendo(null);
            remontar();
            await recarregar();
          }}
        />
      )}
      <Drawer aberto={gerindo} aoFechar={() => setGerindo(false)} largura="lg" titulo={t('funis.titulo')}>
        <GerirFunis aoCriar={(id) => escolherFunil(id)} />
      </Drawer>
      <Drawer
        aberto={novo}
        aoFechar={() => setNovo(false)}
        largura="lg"
        titulo={t('acoes.novaOportunidade')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setNovo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-oportunidade">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <FormOportunidade
          usuarioId={s.usuario.id}
          funilId={funilId || undefined}
          aoSalvar={async (r) => {
            setNovo(false);
            toast.sucesso(tn('comum.salvo'));
            await recarregar();
            if (r.id) abrir(r.id);
          }}
        />
      </Drawer>
    </>
  );
}
